import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMount } from 'react-use';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useQueryParams } from '@/hooks/useQueryParams';
import type { ScheduleLocationState } from '@/pages/Schedule/types';
import type { LaunchOption, LaunchParams } from './types';
import { useLauncherStore } from './useLauncherStore';
import { LAUNCH_PARAM_PREFIX, getLaunchParams } from './util';

/**
 * Determines which workflow to launch when the app loads based on state
 * configured via the login page or query param values.
 */
export const useLauncher = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { queryParams, removeQueryParam } = useQueryParams();
  const { launch, setLaunch, launchParams, reset } = useLauncherStore();
  const state = location.state as undefined | ScheduleLocationState;

  useMount(() => {
    // Read fresh rather than trusting the `launch` destructured above: this
    // effect's closure is fixed at this component's first render, so on a
    // page that mounts many `useLauncher` consumers in the same commit (one
    // `useLauncherAction` per event card in a long `EntryList`, say), every
    // one of them still sees the pre-hydration `null` here even after an
    // earlier sibling's effect already set it. Reading the store directly
    // catches that update; the stale render-time `launch` above cannot. Every
    // instance racing past it called `removeQueryParam` and stripped the same
    // param again, well past `history.replaceState`'s throttle
    // (Sentry WEB-CLIENT-2AQ).
    if (useLauncherStore.getState().launch) {
      // The store already has a value. Another consumer of `useLaunchAction`
      // caused the store to hydrate so we don't need to do anything.
      return;
    }

    const launchParam = queryParams.get('launch') as LaunchOption;

    if (launchParam) {
      const params = getLaunchParams(Object.fromEntries(queryParams.entries()));
      setLaunch(launchParam, params);
      removeQueryParam(
        [
          'launch',
          ...Object.keys(params).map((key) => `${LAUNCH_PARAM_PREFIX}${key}`),
        ],
        { replace: true }
      );
    } else if (state?.launch) {
      setLaunch(state.launch, getLaunchParams((state as LaunchParams) || {}));
    }
  });

  // Non-reactive, one-shot read of the current launch value. Useful when a
  // consumer needs the value inside an effect without subscribing to changes.
  const getLaunch = useCallback(() => useLauncherStore.getState().launch, []);

  // after the `launch` value is consumed, it should be cleared so another
  // render cycle doesn't re-trigger the launch action
  const clearLaunch = useCallback(() => {
    reset();

    // location state persists after reloads so clear that as well
    if (state?.launch) {
      const nextState = { ...state };
      delete nextState.launch;
      navigate(location, { replace: true, state: nextState });
    }
  }, [reset, navigate, location, state]);

  return { launch, getLaunch, clearLaunch, launchParams, setLaunch };
};

type LauncherActionOptions = {
  // When false, the launch action is left unconsumed so the callback can be
  // deferred until some prerequisite state (e.g. the profile query) resolves.
  enabled?: boolean;
};

/**
 * Utility hook for performing a side effect on a particular `LaunchOption`.
 *
 * Ex:
 * useLauncherAction('onboarding', () => {
 *   openOnboardingModal();
 * });
 */
export const useLauncherAction = (
  action: LaunchOption,
  callback: (launchParams: LaunchParams) => void,
  { enabled = true }: LauncherActionOptions = {}
) => {
  const { launch, clearLaunch, launchParams } = useLauncher();
  const { trackEvent } = useAnalytics();
  const callbackRef = useRef(callback);
  const fired = useRef(false);

  useEffect(() => {
    callbackRef.current = callback;
  });

  useEffect(() => {
    if (enabled && !fired.current && action === launch) {
      callbackRef.current(launchParams);
      fired.current = true;
      clearLaunch();
      // Report the resumed launch action (e.g. the post-checkout redirect
      // firing `share-modal`) so it can anchor Amplitude funnels.
      if (launch) {
        trackEvent('launcher:action', { action });
      }
    }
  }, [action, clearLaunch, enabled, launch, launchParams, trackEvent]);
};
